import { useMemo } from 'react';
import { cn } from '@rocco/utils/cn';
import type { MeUser } from '~/server/types';
import type { Conversation } from '../../types';
import { getChatParticipants } from '../../utils';
import { useConversationActiveStatus } from '../../hooks/use-conversation-active-status';
import { ParticipantAvatars } from '../avatar/ParticipantAvatars';
import { ThumbnailAvatar } from '../avatar/ThumbnailAvatar';

/**
 * The avatar component for a chat box. This is determined by the conversation
 * type, participants, and thumbnail settings.
 * @see {@link ChatBox}
 */
export function ChatBoxAvatar({
    conversation,
    user,
    isOpen,
}: {
    conversation?: Conversation;
    user: MeUser;
    isOpen: boolean;
}) {
    const isDirectConversation =
        conversation?.__typename === 'DirectConversation';
    const isGroupChat = isDirectConversation && conversation.IsGroupChat;
    const participants = useMemo(() => {
        return isGroupChat
            ? getChatParticipants(conversation)
            : getChatParticipants(conversation, user.ID);
    }, [isGroupChat, conversation, user.ID]);
    const activeStatus = useConversationActiveStatus(conversation);

    return isDirectConversation && conversation.Thumbnail?.Link ? (
        <ThumbnailAvatar
            className={cn(
                isOpen
                    ? 'size-[40px]'
                    : 'size-14 border-2 border-white shadow-lg'
            )}
            src={conversation.Thumbnail.Link}
            activeStatus={activeStatus ?? undefined}
        />
    ) : (
        <ParticipantAvatars
            className={cn(
                isOpen
                    ? participants.length > 3
                        ? 'space-x-[-20px]'
                        : 'space-x-[-16px]'
                    : '-space-x-9 rounded-full bg-white p-0.5 shadow-lg',
                // Add right padding to compensate for the missing border.
                !isOpen && participants.length > 4 && 'pr-1'
            )}
            avatarClassName={cn(
                isOpen
                    ? 'size-[40px]'
                    : 'size-14 border-2 border-white shadow-lg'
            )}
            fallbackClassName={cn(isOpen ? '!text-[18px]' : '!text-6')}
            extraClassName={cn(
                // Make smaller to compensate for the missing border.
                !isOpen && 'size-[calc(3.5rem-4px)] border-none'
            )}
            participants={participants}
            activeStatus={activeStatus ?? undefined}
            maxAvatars={isGroupChat ? 4 : 1}
        />
    );
}
